import { z } from 'zod';

/**
 * ko-admin#213 — `GET /admin/platform/status` (ko-backend#939, contract on
 * ko-backend#919): the Panel's row of cards in one read. ADMIN only.
 *
 * Every closed set is read as `z.string()` and every object passes unknown
 * keys through: a module, a policy source or a card the backend adds tomorrow
 * must fall back to a generic label, not fail the parse of the whole Panel.
 * The keys the backend added on top of the spec (`catalog`, `paused`,
 * `activatedAt`, `commissionSource`) are optional for the same reason.
 *
 * ko-admin#273 — since ko-backend#980 (#975) the route answers STAFF too, cut
 * card by card: every card whose list the reader cannot open comes as `null`,
 * and so does its queue. So every card is `nullish()`: a card that comes
 * `null`, or not at all, hides itself instead of failing the parse of the
 * whole Panel. `audience`, `proposalsPending` and `queues` are absent on a
 * backend older than #980.
 */
const Count = z.number().int().nonnegative();

export const PlatformStatusModule = z
  .object({
    key: z.string(),
    label: z.string(),
    enabled: z.boolean(),
    /** A row exists; otherwise the switch is on by default. */
    configured: z.boolean(),
    changedBy: z.object({ id: z.string(), displayName: z.string() }).passthrough().nullish(),
    changedAt: z.string().nullish(),
  })
  .passthrough();
export type PlatformStatusModule = z.infer<typeof PlatformStatusModule>;

export const PlatformStatusPolicy = z
  .object({
    module: z.string(),
    version: z.number(),
    activatedAt: z.string().nullish(),
    /** `POLICY` | `DEFAULT_LADDER` | `MALFORMED`; null when there is no active version. */
    commissionSource: z.string().nullish(),
    hasCommissionTable: z.boolean(),
  })
  .passthrough();
export type PlatformStatusPolicy = z.infer<typeof PlatformStatusPolicy>;

/**
 * One queue of «Lo que espera a una persona»: the card's own count and the
 * date its oldest item started waiting (`null` only when `count` is 0).
 */
export const PlatformStatusQueue = z
  .object({ count: Count, oldestAt: z.string().nullable() })
  .passthrough();
export type PlatformStatusQueue = z.infer<typeof PlatformStatusQueue>;

const Queue = PlatformStatusQueue.nullish();

/** ko-backend#980 — every queue the Panel lists, keyed as the backend names it. */
const QUEUES = {
  pendingDecisions: Queue,
  slaBreaches: Queue,
  disputesOpen: Queue,
  tradesUnderReview: Queue,
  frozenOrders: Queue,
  frozenTrades: Queue,
  clawbacksPending: Queue,
  payoutsPending: Queue,
  tournamentsPendingApproval: Queue,
  proposalsPending: Queue,
  outboxStuck: Queue,
  // ko-backend#1273 — the desks worked every day: identity checks a provider
  // could not decide, listings and store applications waiting for a reviewer,
  // user reports nobody has read, and the three moments a KO Hub case waits
  // on the office. Absent on an older backend.
  kycPending: Queue,
  listingsPendingReview: Queue,
  storesPending: Queue,
  reportsOpen: Queue,
  kohubRequested: Queue,
  kohubInOffice: Queue,
  kohubIncidentsOpen: Queue,
};
export type PlatformStatusQueueKey = keyof typeof QUEUES;

export const PlatformStatus = z
  .object({
    generatedAt: z.string(),
    /** Who the answer was cut for; a STAFF answer carries `null` cards. */
    audience: z.string().nullish(),
    modules: z.array(PlatformStatusModule).nullish(),
    /** `blocked` is a subset of `off`: dormant keys that wait on another decision. */
    dormant: z
      .object({ total: Count, on: Count, off: Count, blocked: Count })
      .passthrough()
      .nullish(),
    catalog: z.object({ total: Count, configured: Count }).passthrough().nullish(),
    /** Stored rows the catalogue would reject today (unknown key, wrong type, out of range). */
    drift: z
      .object({ count: Count, keys: z.array(z.string()) })
      .passthrough()
      .nullish(),
    /** Same rule as the tray's `total`: cases still open, not audit rows. */
    pendingDecisions: Count.nullish(),
    /** `oldestHours`: how far past its deadline the worst live breach is. */
    slaBreaches: z
      .object({ open: Count, oldestHours: z.number().nullable() })
      .passthrough()
      .nullish(),
    outboxStuck: Count.nullish(),
    tournaments: z
      .object({
        pendingApproval: Count,
        inscriptionOpen: Count,
        live: Count,
        paused: Count.nullish(),
        readyGate: z.boolean(),
      })
      .passthrough()
      .nullish(),
    escrow: z
      .object({ frozenOrders: Count, frozenTrades: Count, clawbacksPending: Count })
      .passthrough()
      .nullish(),
    tradesUnderReview: Count.nullish(),
    policies: z.array(PlatformStatusPolicy).nullish(),
    /** Creator proposals waiting for review. */
    proposalsPending: Count.nullish(),
    /** ko-backend#1273 — users parked in manual KYC review. */
    kycPending: Count.nullish(),
    /** ko-backend#1273 — review rows still without a decision. */
    listingsPendingReview: Count.nullish(),
    /** ko-backend#1273 — store applications still pending. */
    storesPending: Count.nullish(),
    /** ko-backend#1273 — user reports still open. */
    reportsOpen: Count.nullish(),
    /** ko-backend#1273 — what waits on the KO Hub office. */
    kohub: z
      .object({ requested: Count, inOffice: Count, incidentsOpen: Count })
      .passthrough()
      .nullish(),
    queues: z.object(QUEUES).passthrough().nullish(),
  })
  .passthrough();
export type PlatformStatus = z.infer<typeof PlatformStatus>;
